import type { Dispatch, RefObject, SetStateAction } from 'react';
import { X, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SortByUploadedByDropdown } from '@/components/SortByUploadedByDropdown';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import type { SortBy, UserOption } from '../utils';

interface AlbumControlsProps {
  showSort: boolean;
  sortBy: SortBy;
  setSortBy: (sortBy: SortBy) => void;
  canSortByVotes: boolean;
  canSortByComments: boolean;
  isSortDropdownOpen: boolean;
  setIsSortDropdownOpen: (value: boolean) => void;
  sortDropdownTriggerRef: RefObject<HTMLButtonElement | null>;
  users: UserOption[];
  selectedUsers: Set<string>;
  setSelectedUsers: Dispatch<SetStateAction<Set<string>>>;
  hideMyPhotos: boolean;
  setHideMyPhotos: Dispatch<SetStateAction<boolean>>;
  currentUserId?: string;
  isFilterDropdownOpen: boolean;
  setIsFilterDropdownOpen: (value: boolean) => void;
  filterDropdownTriggerRef: RefObject<HTMLButtonElement | null>;
  filtersBarRef: RefObject<HTMLDivElement | null>;
  chipsContainerRef: RefObject<HTMLDivElement | null>;
  t: (key: string) => string;
}

const AlbumControls = ({
  showSort,
  sortBy,
  setSortBy,
  canSortByVotes,
  canSortByComments,
  isSortDropdownOpen,
  setIsSortDropdownOpen,
  sortDropdownTriggerRef,
  users,
  selectedUsers,
  setSelectedUsers,
  hideMyPhotos,
  setHideMyPhotos,
  currentUserId,
  isFilterDropdownOpen,
  setIsFilterDropdownOpen,
  filterDropdownTriggerRef,
  filtersBarRef,
  chipsContainerRef,
  t,
}: AlbumControlsProps) => {
  return (
    <div className="space-y-4 mb-6" ref={filtersBarRef}>
      <div className="flex flex-row justify-between items-center gap-4">
        {showSort && (
          <div className="flex items-center gap-2">
            <DropdownMenu open={isSortDropdownOpen} onOpenChange={setIsSortDropdownOpen}>
              <DropdownMenuTrigger asChild>
                <Button ref={sortDropdownTriggerRef} variant="outline" size="sm" className="justify-between px-2 text-xs ">
                  {sortBy === 'comments' ? t('mostCommentedFirst') : sortBy === 'votes' ? t('mostVotedFirst') : t('newest')}
                  <ChevronDown className="ml-2 h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-[180px] bg-background border border-border z-20 flex flex-col space-y-2">
                <DropdownMenuItem
                  onClick={() => setSortBy('recent')}
                  className={sortBy === 'recent' ? 'bg-accent text-accent-foreground text-xs ' : 'text-xs '}
                >
                  {t('newest')}
                </DropdownMenuItem>
                {canSortByVotes && (
                  <DropdownMenuItem
                    onClick={() => setSortBy('votes')}
                    className={sortBy === 'votes' ? 'bg-accent text-accent-foreground text-xs ' : 'text-xs '}
                  >
                    {t('mostVotedFirst')}
                  </DropdownMenuItem>
                )}
                {canSortByComments && (
                  <DropdownMenuItem
                    onClick={() => setSortBy('comments')}
                    className={sortBy === 'comments' ? 'bg-accent text-accent-foreground text-xs ' : 'text-xs '}
                  >
                    {t('mostCommentedFirst')}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}

        <SortByUploadedByDropdown
          className="ml-auto"
          users={users}
          selected={selectedUsers}
          onChange={(next) => setSelectedUsers(new Set(next))}
          currentUserId={currentUserId}
          onHideMineClick={() => setHideMyPhotos(true)}
          showHideMineAction={Boolean(currentUserId)}
          hideMineActive={hideMyPhotos}
          onClearAll={() => setHideMyPhotos(false)}
          t={t}
          isOpen={isFilterDropdownOpen}
          onOpenChange={setIsFilterDropdownOpen}
          triggerRef={filterDropdownTriggerRef}
        />
      </div>

      {(selectedUsers.size > 0 || hideMyPhotos) && (
        <div className="flex flex-wrap gap-2 justify-end" ref={chipsContainerRef}>
          {hideMyPhotos && (
            <div className="flex items-center gap-2 bg-muted rounded-full px-3 py-1">
              <span className="text-sm">{t('hideMinePhotosActive')}</span>
              <button
                onClick={() => setHideMyPhotos(false)}
                className="ml-1 hover:bg-background rounded-full p-0.5"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          )}
          {Array.from(selectedUsers).map((userId) => {
            const user = users.find((u) => u.id === userId);
            if (!user) return null;

            return (
              <div key={userId} className="flex items-center gap-2 bg-accent text-accent-foreground rounded-full px-3 py-1">
                {user.avatar ? (
                  <img
                    src={user.avatar}
                    alt={user.name}
                    className="w-5 h-5 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-5 h-5 rounded-full bg-background flex items-center justify-center text-xs">
                    {user.name.charAt(0).toUpperCase()}
                  </div>
                )}
                <span className="text-sm">{user.name}</span>
                <button
                  onClick={() => {
                    setSelectedUsers((prev) => {
                      const newSet = new Set(prev);
                      newSet.delete(userId);
                      return newSet;
                    });
                  }}
                  className="ml-1 hover:bg-accent-foreground hover:text-accent rounded-full p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AlbumControls;
